import { type ChatClientNotePreview, CLIENT_NOTE_MESSAGES } from '@fos/shared/contracts/client-notes';
import { useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../../lib/api';
import { trpc } from '../../lib/trpc';
import { Button, FormMessage, Markdown } from '../../ui';
import { useNotesInvalidate } from './ClientNotes';

/**
 * Notatka klienta z Copilota (`add_client_note`) w rozmowie z danymi klienta (Fala 10): nic nie jest zapisane, dopóki
 * osoba nie potwierdzi — treść mogła pochodzić z polecenia ukrytego w mailu albo notatce. Zapis = `history.notes.add`
 * z UI (źródło „Ręcznie”: to osoba zatwierdziła treść).
 */
export function ChatNoteConfirm({ preview, onDone }: { preview: ChatClientNotePreview; onDone?: () => void }) {
  const add = trpc.history.notes.add.useMutation();
  const invalidate = useNotesInvalidate();
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setError(null);
    try {
      await add.mutateAsync({
        clientId: preview.clientId,
        bodyMd: preview.bodyMd,
        ...(preview.projectId ? { projectId: preview.projectId } : {}),
        ...(preview.taskId ? { taskId: preview.taskId } : {}),
        ...(preview.meetingId ? { meetingId: preview.meetingId } : {}),
        ...(preview.pinned ? { pinned: true } : {}),
      });
      setDone(true);
      void invalidate();
      onDone?.();
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się dodać notatki.'));
    }
  };
  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-canvas p-3">
      <p className="text-caption text-fg-muted">Notatka dla: {preview.clientName}</p>
      <Markdown className="line-clamp-6 text-body-s break-words">{preview.bodyMd}</Markdown>
      <p className="text-caption text-fg-muted">{CLIENT_NOTE_MESSAGES.needsConfirmation}</p>
      {done ? (
        <p className="text-caption text-fg">
          Dodano notatkę{' '}
          <Link to={`/klienci/${preview.clientId}?zakladka=historia`} className="text-accent hover:underline">
            Otwórz
          </Link>
        </p>
      ) : (
        <div>
          <Button size="s" variant="secondary" loading={add.isPending} onClick={() => void confirm()}>
            Dodaj notatkę
          </Button>
        </div>
      )}
      {error && <FormMessage>{error}</FormMessage>}
    </div>
  );
}
